/**
 * Mobile grenade throw-strength selector (MOB-W4).
 *
 * Desktop uses LMB=overhand, RMB=underhand to pick a throw strength.
 * Mobile has only one grenade button, so we map a vertical drag gesture
 * over that button into the same 3-way strength enum that
 * `gameplay/grenade.ts:getThrowSpeed(strength)` consumes:
 *   0 = overhand, 1 = medium, 2 = underhand
 *
 * This module is INPUT-ONLY logic. It owns no DOM, listeners, or state
 * beyond an accumulated Y delta. The integrator (TouchInputProvider /
 * game.ts mobile branch) feeds pointer/touchmove deltas in here and
 * reads `getStrength()` at throw time, then calls `reset()` after the
 * throw so the next grenade defaults back to medium.
 *
 * Boundary semantics: strict greater-than against the threshold. A
 * delta of exactly +/-THRESHOLD stays medium; only |dy| > THRESHOLD
 * crosses into overhand (dy < -THRESHOLD) or underhand (dy > THRESHOLD).
 */

/** Pixel threshold a vertical swipe must exceed to leave the medium bucket. */
export const GRENADE_STRENGTH_THRESHOLD_PX = 40;

/** Throw strength enum matching `gameplay/grenade.ts:getThrowSpeed`. */
export type GrenadeStrength = 0 | 1 | 2;

export class GrenadeStrengthSelector {
  private dy = 0;

  /** Accumulate a vertical pointer/touch delta (positive = down on screen). */
  addDeltaY(dy: number): void {
    this.dy += dy;
  }

  /** Derive the current throw strength from the accumulated delta. */
  getStrength(): GrenadeStrength {
    if (this.dy < -GRENADE_STRENGTH_THRESHOLD_PX) return 0;
    if (this.dy > GRENADE_STRENGTH_THRESHOLD_PX) return 2;
    return 1;
  }

  /** Clear the accumulated delta; next read returns medium. */
  reset(): void {
    this.dy = 0;
  }

  /** Current accumulated Y delta — useful for HUD preview / debugging. */
  getDeltaY(): number {
    return this.dy;
  }
}
